import { HttpErrorResponse } from '@angular/common/http'
import { computed, Injectable, signal } from '@angular/core'

import { Brand } from '../models/brand.entity'
import { BrandsService } from '../brands.service'
import { BrandsRepository } from '../brands.repository'
import { BrandData } from '../models/brand-listing.model'

export interface BrandDetailState {
  brand?: Brand
  productsCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isModified: boolean
}

const BRAND_DETAIL_INITIAL_STATE: BrandDetailState = {
  isLoading: false,
  isModified: false,
}

@Injectable()
export class BrandDetailStore {
  constructor(
    private brandsService: BrandsService,
    private brandsRepository: BrandsRepository,
  ) {}

  private state = signal<BrandDetailState>(BRAND_DETAIL_INITIAL_STATE)

  // Selectors
  readonly brand = computed(() => this.state().brand)
  readonly productsCount = computed(() => this.state().productsCount)
  readonly isModified = computed(() => this.state().isModified)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isErrored = computed(() => !!this.state().error)

  // Effects

  async load(brandId?: string): Promise<void> {
    if (!brandId) {
      return
    }
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.brandsService.read$(brandId).subscribe({
        next: async (data) => {
          const extData = await this.getData(brandId)
          resolve()
          this.setData(data, extData)
        },
        error: (error) => {
          reject(error)
          this.setError(error)
        },
      })
    })
  }

  async getData(brandId: string): Promise<BrandData | undefined> {
    return new Promise((resolve) => {
      this.brandsService.loadBrandData$(brandId).subscribe({
        next: (data) => {
          resolve(data)
        },
        error: (error) => {
          resolve(undefined)
          this.setError(error)
        },
      })
    })
  }

  async save(brand: Brand): Promise<boolean> {
    this.unsetError()

    return new Promise((resolve) => {
      this.brandsService.upsert$(brand).subscribe({
        next: (data) => {
          resolve(true)
          this.setBrand(data)
          this.brandsRepository.notify({ code: 'BRAND_SAVED' })
        },
        error: (error) => {
          resolve(false)
          this.setError(error)
        },
      })
    })
  }

  async delete(brand: Brand): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.brandsRepository.alert('BRAND_DELETION', brand).then((response) => {
        if (response === 'success') {
          this.setIsLoading(true)
          this.brandsService.delete$(brand._id).subscribe({
            next: () => {
              resolve(true)
              this.brandsRepository.notify({
                code: 'BRAND_DELETED',
              })
            },
            error: (error: HttpErrorResponse) => {
              resolve(false)
              this.setError(error)
            },
          })
        }
      })
    })
  }

  // Reducers

  setData(brand: Brand, data?: BrandData, isModified?: boolean) {
    this.state.set({
      ...this.state(),
      brand: brand,
      productsCount: data?.productsCount,
      isLoading: false,
      isModified: isModified ?? false,
    })
  }

  setBrand(brand: Brand) {
    this.state.set({
      ...this.state(),
      brand,
      isLoading: false,
      isModified: false,
    })
  }

  updateBrand(brand: Brand) {
    this.state.set({
      ...this.state(),
      brand,
      isModified: true,
      isLoading: false,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }
}
